// Pure builders for the materials CSV export (settings > Data). No Firestore,
// no React — the I/O lives in the settings Data component.

import { isAdvice } from './materialCatalog'
import materialDisplayName, { materialArticleNumber } from './materialDisplayName'
import materialQuantities from './materialQuantities'
import { unitPriceFor } from './computeHouseholdMaterials'
import { buildStreetLabelMap, nameLabel, phoneLabel } from '../adressHelpers/buildStreetLabels'
import buildAddressLabels from '../adressHelpers/buildAdressLabelMap'

// Map AdressID -> "Straat Huisnummer, Woonplaats", falling back to the
// household Title when the address is unknown. Dezelfde lezing als de
// keuzelijst in een afspraak (vaste gegevens, tot de migratie de oude regels).
// AI-CONTEXT: See docs/features/huishouden-vaste-gegevens.md (T9)
const buildAdressLabelMap = (personaData = [], adresses = []) => {
    const live = (Array.isArray(adresses) ? adresses : []).filter((adress) => adress && adress.ID)
    const labels = buildAddressLabels(personaData, live)
    const map = {}
    for (const adress of live) map[adress.ID] = labels[adress.ID] || adress.Title || ''
    return map
}

// Map AdressID -> { Naam, Telefoonnummer } for the export's contact columns.
//
// Leunt op dezelfde naam- en telefoondefinitie als de schermen, zodat de
// leverancier dezelfde bewoner voor zich krijgt als de fixer in de app.
// AI-CONTEXT: See docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook
//
// De placeholder is bewust leeg: het em-streepje van de schermen is in een CSV
// een waarde in plaats van 'niet ingevuld'.
const buildAdressContactMap = (personaData = [], adresses = []) => {
    const byAdress = buildStreetLabelMap(personaData, adresses)

    const map = {}
    for (const id of Object.keys(byAdress)) {
        map[id] = {
            Naam: nameLabel(byAdress, id, ''),
            Telefoonnummer: phoneLabel(byAdress, id, ''),
        }
    }
    return map
}

// Join AdressMaterials entries with the master Materials list and address
// labels into CSV-ready rows with Dutch headers. Soft-deleted entries are
// excluded; rows are grouped per address (sorted on label).
const buildMaterialsExportRows = ({ entries = [], materials = [], adressLabels = {}, adressContacts = {} }) => {
    const materialsByID = new Map()
    for (const m of materials) {
        if (m && m.ID) materialsByID.set(m.ID, m)
    }

    const live = entries.filter((e) => e && e.Deleted !== true && e.Deleted !== 'true')

    const rows = live.map((entry) => {
        const material = materialsByID.get(entry.MaterialID)
        // Aantal en status uit materialQuantities: gebruikt als ingevuld, anders mee te nemen.
        // AI-CONTEXT: See docs/features/household-materials.md (Uitbreiding 2026-10)
        const aantallen = materialQuantities(entry)
        const quantity = aantallen.impact
        // Dezelfde prijs als het overzicht: die bij registratie (T20).
        const { unitPrice } = unitPriceFor(entry, material)
        const contact = adressContacts[entry.AdressID] || {}
        return {
            Adres: adressLabels[entry.AdressID] || '',
            // Naast het adres en niet achteraan: de leverancier belt het
            // huishouden voor een inmeetafspraak en leest die drie samen.
            Naam: contact.Naam || '',
            Telefoonnummer: contact.Telefoonnummer || '',
            Materiaal: materialDisplayName(material) || 'Onbekend materiaal',
            // Een eigen kolom en niet in de naam geplakt: de ontvanger bestelt
            // op dit nummer en wil erop kunnen sorteren en filteren.
            Productnummer: materialArticleNumber(material),
            // De lijst bevat twee soorten regels. Zonder deze kolom leest een
            // gegeven coachadvies in de export als een uitgedeeld product.
            Soort: isAdvice(material) ? 'Coachadvies' : 'Materiaal',
            Categorie: material?.Category || '',
            Aantal: quantity,
            Eenheid: material?.Unit || '',
            Status: aantallen.status === 'done' ? 'Gebruikt' : 'Gepland',
            Omschrijving: entry.Description || '',
            'Prijs per stuk': unitPrice,
            Subtotaal: unitPrice * quantity,
        }
    })

    return rows.sort((a, b) => a.Adres.localeCompare(b.Adres, 'nl'))
}

export { buildAdressLabelMap, buildAdressContactMap, buildMaterialsExportRows }
